import React from "react";
import {
  Boxes, CalendarClock, Factory, Ship, ClipboardCheck, FileText,
  Users, MessagesSquare, ArrowRight, X, Check,
} from "lucide-react";
import { Reveal, SectionHeading, cardBase } from "./primitives";

const CHALLENGES = [
  { icon: Boxes, label: "Large purchase volumes" },
  { icon: CalendarClock, label: "Production scheduling" },
  { icon: Factory, label: "Factory coordination" },
  { icon: Ship, label: "Freight planning" },
  { icon: ClipboardCheck, label: "Inspection management" },
  { icon: FileText, label: "Document handling" },
  { icon: Users, label: "Supplier follow-ups" },
  { icon: MessagesSquare, label: "Disconnected communication" },
];

export default function Problem() {
  return (
    <section className="relative bg-[#F8FAFC] py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal>
          <SectionHeading
            eyebrow="The Problem"
            title="Managing large bulk orders shouldn't be complicated."
            subtitle="Buyers juggling high-volume factory purchases end up stitching together spreadsheets, email threads and phone calls across every stage of the operation."
          />
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Reveal>
            <div className={`${cardBase} h-full p-8`}>
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-rose-50 text-rose-500">
                  <X className="h-4 w-4" strokeWidth={2.5} />
                </span>
                <h3 className="font-display text-lg font-bold text-[#0A1128]">
                  Fragmented bulk purchasing
                </h3>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {CHALLENGES.map((c) => (
                  <div
                    key={c.label}
                    className="flex items-center gap-3 rounded-xl border border-slate-200/70 bg-slate-50/50 px-3.5 py-3"
                  >
                    <c.icon className="h-4 w-4 shrink-0 text-[#94A3B8]" />
                    <span className="text-[13px] font-medium text-[#475569]">{c.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative h-full overflow-hidden rounded-2xl bg-[#0A1128] p-8 shadow-[0_30px_60px_-20px_rgba(10,17,40,0.5)]">
              <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[#0052FF]/30 blur-3xl" aria-hidden />
              <div className="relative">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#10B981]/15 text-[#34D399]">
                    <Check className="h-4 w-4" strokeWidth={3} />
                  </span>
                  <h3 className="font-display text-lg font-bold text-white">One connected Workspace</h3>
                </div>
                <p className="mt-5 text-[15px] leading-relaxed text-slate-300">
                  Every stage of your bulk order — production, inspection, freight and shipment —
                  lives in a single Workspace with real-time visibility.
                </p>

                <div className="mt-7 space-y-2.5">
                  {["Select product & manufacturer", "Confirm volume & production plan", "Inspection & freight coordination", "Live shipment tracking to delivery"].map(
                    (step, i) => (
                      <div
                        key={step}
                        className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 backdrop-blur-sm"
                      >
                        <span className="font-mono-ui flex h-6 w-6 items-center justify-center rounded-md bg-[#0052FF] text-[11px] font-bold text-white">
                          {i + 1}
                        </span>
                        <span className="text-[13px] font-medium text-slate-100">{step}</span>
                        <ArrowRight className="ml-auto h-3.5 w-3.5 text-slate-500" />
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
